import { AfterViewInit, Component, EventEmitter, Input, Output, OnInit, SimpleChanges, Inject, ChangeDetectorRef, NgZone } from '@angular/core';
import { Global } from 'src/app/core/resources/global';
import { Utils } from 'src/app/core/resources/utils';
import { CargoDetailService } from 'src/app/modules/cargo/cargo-detail/cargo-detail.service';
import { v4 as uuidv4 } from 'uuid';
import { GoogleService } from 'src/app/core/services/google.service';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { MapsEnum } from 'src/app/core/enums/maps.enum';
import { NgxSpinnerService } from 'ngx-spinner';
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { MapLocation, MapLocations } from 'src/app/core/interfaces/mapLocations';
import { MapPoint } from 'src/app/core/interfaces/mapPoint';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { LocationAddress } from 'src/app/core/interfaces/locationAddress';
import { CriticZone } from 'src/app/core/interfaces/critic-zone';
import { PlanningRouteService } from 'src/app/core/services/planning-route.service';
import { buildCriticZoneInfoWindow } from "src/app/core/resources/critic-zone-info-window";

@Component({
  selector: 'app-standard-map',
  templateUrl: './standard-map.component.html',
  styleUrls: ['./standard-map.component.scss']
})
export class StandardMapComponent implements OnInit, AfterViewInit {
  // Properties
  @Input('route-google') routeGoogle: any[] = [];
  @Input() options: OptionsAutocomplete;
  // Events
  @Output() clickMap: EventEmitter<any> = new EventEmitter();
  @Output() mapReady: EventEmitter<void> = new EventEmitter();
  // Maps config
  private uuid: string;
  private geofence_references = [];
  public get mapId(): string { return this.uuid; };
  public mapOptions: google.maps.MapOptions;
  public map: google.maps.Map;
  center: google.maps.LatLngLiteral = { lat: 4.687631140720726, lng: -74.12697035582234 };
  polylines = {};
  polylinesGoogle = {};
  directionsRenderersGoogle = {};
  markers = {};
  infoContentMarkers = {};
  localOptions: OptionsAutocomplete = {}
  listMarkersKey: Array<string> = [];
  listPolylineKeys: Array<string> = [];
  listPolylineGoogleKeys: Array<string> = [];
  clickMapMarker;
  clickMapInstance: google.maps.MapsEventListener | null;
  markersCluster: MarkerClusterer;
  private criticZoneCorridors: { [id: string]: google.maps.Polyline[] } = {};
  private criticZoneSignatures: { [prefix: string]: string } = {};

  // Polígonos de municipios seleccionados (validación geográfica).
  // Múltiples polígonos coexisten identificados por key (ej. 'origin', 'dest:11001').
  private municipalityPolygons: Map<string, google.maps.Polygon> = new Map();
  constructor(
    public utils: Utils,
    private freightForwarderDetailService: CargoDetailService,
    private global: Global,
    private googleService: GoogleService,
    public spinner: NgxSpinnerService,
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private planningRouteService: PlanningRouteService,
  ) {
    this.uuid = uuidv4();
  }
  /**
  * This method initiate variables according to the options such as: route, real route, click maps and markers.
  */
  ngOnInit() {
    // Podemos usar esto para inicialización temprana si es necesario
  }
  ngAfterViewInit() {
    // Usar Promise.resolve() para ejecutar después del ciclo de detección de cambios
    Promise.resolve().then(() => {
      this.initMap();

      if (this.options.initialClickMap && this.options.clickMapIcon && this.map)
        this.clickMapListener();
      if (this.options.initialRoute)
        this.processRouteGoogle(this.options.polyline, this.options.cargo);
      if (this.options.initialRealRoute)
        this.checkPolyline(this.options.polyline, 'real', '#02d7dc');
      if (this.options.initialEncodedPolyline && this.options.encodedPolyline)
        this.options.encodedPolylineAsDirections
          ? this.renderEncodedPolylineAsDirections(
            this.options.encodedPolyline,
            'encoded-route',
            this.options.encodedPolylineColor || '#584796',
            this.options.encodedPolylineWaypointsLimit || 20
          )
          : this.renderEncodedPolyline(this.options.encodedPolyline, 'encoded-route', this.options.encodedPolylineColor || '#584796');
      if (this.options.initialMarkers)
        this.checkLocations(this.options.route);
      this.processInitialRoutePlanItinerary();
      if (this.options.singlePoint)
        this.renderSinglePoint(this.options.singlePoint.lat, this.options.singlePoint.lng, {
          key: this.options.singlePoint.key || 'single-point',
          icon: this.options.singlePoint.icon,
          label: this.options.singlePoint.label,
          infoWindow: this.options.singlePoint.infoWindow,
          center: this.options.singlePoint.center !== undefined ? this.options.singlePoint.center : true,
          zoom: this.options.singlePoint.zoom || 15,
          clearPrevious: true
        });
    });
  }
  /**
  * This method check locations, markers, geofences and listeners.
  * @param {SimpleChanges} changes (SimpleChanges) is the element that listen all the changes of the inputs of the map.
  */
  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.options && changes.options.currentValue) {
      if (changes.options.currentValue.polyline && JSON.stringify(this.localOptions['polyline']) !== JSON.stringify(changes.options.currentValue.polyline)) {
        this.localOptions['polyline'] = this.utils.clone(changes.options.currentValue.polyline);
        this.checkPolyline(changes.options.currentValue.polyline, 'real', '#02d7dc');
        if (changes.options.currentValue.cargo && JSON.stringify(this.localOptions['cargo']) !== JSON.stringify(changes.options.currentValue.cargo)) {
          this.localOptions['cargo'] = this.utils.clone(changes.options.currentValue.cargo);
          this.processRouteGoogle(changes.options.currentValue.polyline, changes.options.currentValue.cargo);
        } else if (changes.options.currentValue.polyline) {
          const locations = changes.options.currentValue.polyline;
          for (const key in locations) {
            if (locations[key] && locations[key].routePlanItinerary && locations[key].showRoutePlan) {
              this.processRoutePlanItinerary(locations[key], key);
            }
          }
        }
      }
      if (changes.options.currentValue.route && JSON.stringify(this.localOptions['route']) !== JSON.stringify(changes.options.currentValue.route)) {
        this.localOptions['route'] = this.utils.clone(changes.options.currentValue.route);
        this.checkLocations(this.localOptions['route']);
      }
      if (changes.options.currentValue.encodedPolyline !== undefined && this.localOptions['encodedPolyline'] !== changes.options.currentValue.encodedPolyline) {
        this.localOptions['encodedPolyline'] = changes.options.currentValue.encodedPolyline;
        if (changes.options.currentValue.encodedPolylineAsDirections) {
          this.renderEncodedPolylineAsDirections(
            changes.options.currentValue.encodedPolyline,
            'encoded-route',
            changes.options.currentValue.encodedPolylineColor || '#584796',
            changes.options.currentValue.encodedPolylineWaypointsLimit || 20
          );
        } else {
          this.renderEncodedPolyline(
            changes.options.currentValue.encodedPolyline,
            'encoded-route',
            changes.options.currentValue.encodedPolylineColor || '#584796'
          );
        }
      }
      if (
        changes.options.currentValue.encodedPolylineAsDirections !== undefined &&
        this.localOptions['encodedPolylineAsDirections'] !== changes.options.currentValue.encodedPolylineAsDirections
      ) {
        this.localOptions['encodedPolylineAsDirections'] = changes.options.currentValue.encodedPolylineAsDirections;
        if (changes.options.currentValue.encodedPolyline) {
          if (changes.options.currentValue.encodedPolylineAsDirections) {
            this.renderEncodedPolylineAsDirections(
              changes.options.currentValue.encodedPolyline,
              'encoded-route',
              changes.options.currentValue.encodedPolylineColor || '#584796',
              changes.options.currentValue.encodedPolylineWaypointsLimit || 20
            );
          } else {
            this.renderEncodedPolyline(
              changes.options.currentValue.encodedPolyline,
              'encoded-route',
              changes.options.currentValue.encodedPolylineColor || '#584796'
            );
          }
        }
      }
      if ((changes.options.currentValue.showGeofences || changes.options.currentValue.showGeofences === false) && this.localOptions['showGeofences'] !== changes.options.currentValue.showGeofences) {
        this.localOptions['showGeofences'] = changes.options.currentValue.showGeofences
        changes.options.currentValue.showGeofences ? this.renderGeofences() : this.hideGeofences();
      }
      if (changes.options.currentValue.removeClickMapListener && this.map) this.removeMapListener();
      if (changes.options.currentValue.clickMap && (changes.options.currentValue.clickMapIcon || changes.options.currentValue.onlyListener) && this.map) this.clickMapListener();
      if (changes.options.currentValue.removeMarker && this.map) this.removeMarker(this.options && this.options.keyMarkerListener ? this.options.keyMarkerListener : '');
    }
    this.cdRef.detectChanges();
  }
  /**
  * This method create a new instance of map.
  */
  initMap() {
    if (document.getElementById(this.mapId)) {
      this.map = new google.maps.Map(document.getElementById(this.mapId) as HTMLElement, {
        center: { lat: this.options && this.options.lat ? this.options.lat : 4.687631140720726, lng: this.options && this.options.lng ? this.options.lng : -74.12697035582234 },
        zoom: this.options && this.options.zoom ? this.options.zoom : 13
      });
      this.mapReady.emit();
    }
  }
  /**
  * This method add a listener of events to an specific marker.
  */
  clickMapListener() {
    if (!this.clickMapInstance) {
      this.clickMapInstance = this.map.addListener('click', (e) => {
        if (this.options && !this.options.onlyListener) {
          // Remover el marcador anterior del click
          this.removeMarker(this.options && this.options.keyMarkerListener ? this.options.keyMarkerListener : 'clickMapMarker');

          // Remover el singlePoint si existe
          if (this.options.singlePoint) {
            const singlePointKey = this.options.singlePoint.key || 'single-point';
            this.removeMarker(singlePointKey);
          }

          // Crear el nuevo marcador en la posición clickeada
          this.createMarker(this.options && this.options.keyMarkerListener ? this.options.keyMarkerListener : 'clickMapMarker', e.latLng.lat(), e.latLng.lng(), null, this.options && this.options.clickMapIcon ? this.options.clickMapIcon : null, this.options && this.options.labelIcon ? this.options.labelIcon : null);
        }
        this.setCenterMap(e.latLng.lat(), e.latLng.lng(), 16)
        // El callback de Google Maps corre FUERA de la zona de Angular.
        // Emitimos dentro de ngZone.run para que toda la cadena posterior
        // (onClickMap → geocode → setValue → AsyncValidator geo) corra en la
        // zona y el cambio de `pending` dispare change detection (de lo
        // contrario el overlay "Verificando…" se queda pegado).
        this.ngZone.run(() => this.clickMap.emit(e));
      });
    }
  }
  /**
  * This method remove the active listener.
  */
  removeMapListener() {
    if (this.clickMapInstance) {
      this.clickMapInstance.remove();
      this.clickMapInstance = null;
    }
  }

  //GEOREFERENCE OPTIONS
  /**
  * This method create geofences according to the information of the load.
  */
  renderGeofences() {
    if (this.options && this.options.cargo) {
      var cargo = this.options.cargo;
    }
    if (!cargo) return;
    const addresses = [];
    cargo.cargoFeature.uploadDownload.origin.addresses.forEach((address) => {
      addresses.push(address);
    });
    for (const destination of cargo.cargoFeature.uploadDownload.destination) {
      destination.addresses.forEach((address) => {
        addresses.push(address);
      });
    }
    for (const stop of addresses) {
      const geofence = new google.maps.Circle({
        strokeColor: "#02d7dc",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#02d7dc",
        fillOpacity: 0.35,
        map: this.map,
        center: new google.maps.LatLng(stop.location.lat, stop.location.lng),
        radius: MapsEnum.GEOFENCE_STANDARD_SIZE,
      });
      this.geofence_references.push(geofence);
    }
  }
  /**
  * This method delete the references related with the geofences in the map.
  */
  hideGeofences() {
    this.geofence_references.forEach((geofence) => { geofence.setMap(null); });
  }

  //MARKERS OPTIONS
  /**
  * This method go through the points of a location array and add or remove markers according to the conditions.
  * @param {MapLocations} locations (MapLocations) is the object of location points that wanted to render.
  */
  private checkLocations(locations: MapLocations) {
    if (this.markersCluster) this.markersCluster.clearMarkers();
    let clusterMarkers = [];
    for (let key in locations) {
      if (locations[key].path && locations[key].path.length) {
        locations[key].path.forEach((point: MapPoint) => {
          if (point) {
            this.removeMarker(point.key);
            let lat = point.lat ? point.lat : point.latLong && point.latLong.lat ? point.latLong.lat : null;
            let lng = point.lng ? point.lng : point.latLong && point.latLong.lng ? point.latLong.lng : null;
            if (lat && lng && point.showMarker) {
              this.createMarker(point.key, lat, lng, point.contentInfoWindow, point.icon, point.labelIcon);
              if (point.setCenter && this.map) this.setCenterMap(lat, lng, point.zoom);
              clusterMarkers.push(this.markers[point.key]);
            }
          }
        });
      }
    }
    this.markersCluster = new MarkerClusterer({ markers: clusterMarkers, map: this.map });
  }
  /**
  * This method create a new instance google marker.
  * @param {string} key (string) is the unique identifier of the marker.
  * @param {number} lat (number) is the latitude of the marker.
  * @param {number} lng (number) is the longitude of the marker.
  * @param {string} contentInfoWindow (string - optional) is the content of the infowindow.
  * @param {string} icon (string - optional) is the icon of the marker.
  * @param {string} labelIcon (string - optional) is the label of the icon associate.
  */
  public createMarker(key: string, lat: number, lng: number, contentInfoWindow?: string, icon?: string, labelIcon?: string) {
    const scaledSize = icon && icon.indexOf('trip_alert') >= 0
      ? new google.maps.Size(28, 38)
      : (icon && icon.includes('pin-destination.png')
        ? new google.maps.Size(20, 25)
        : new google.maps.Size(25, 25));
    this.markers[key] = new google.maps.Marker({
      icon: icon ?
        { url: icon, scaledSize: scaledSize } :
        (icon === 'Point' ?
          this.global.pathMarkerVehicleNoLastLocations :
          this.global.pathMarkerVehicle),
      position: { lat: lat, lng: lng },
      map: this.map,
    });
    if (labelIcon) {
      this.markers[key].set("label", {
        text: labelIcon,
        color: 'white', // Color del texto
        fontWeight: 'bold', // Estilo del texto
        fontSize: '16px',
      });
    }
    if (contentInfoWindow) {
      this.createInfoWindow(key, contentInfoWindow, this.markers[key]);
    }
    this.markers[key].setMap(this.map);
    if (!this.listMarkersKey.includes(key)) {
      this.listMarkersKey.push(key);
    }
  }
  /**
  * This method create an info window in a specific marker.
  * @param {string} key (string) is the unique identifier of the marker.
  * @param {string} content (string) is the content of the infowindow.
  * @param {google.maps.Marker} marker (google.maps.Marker) is google marker instance.
  */
  public createInfoWindow(key: string, content: string, marker: google.maps.Marker) {
    this.infoContentMarkers[key] = new google.maps.InfoWindow({
      content,
    });
    marker.addListener("click", () => {
      this.infoContentMarkers[key].open(this.map, marker);
    });
  }
  /**
* This method remove an specific key of the map.
* @param {string} key (string) is the unique identifier of the marker.
*/
  removeMarker(key: string) {
    if (this.markers[key]) {
      this.markers[key].setMap(null);
      delete this.markers[key];
    }
    if (this.infoContentMarkers[key]) {
      this.infoContentMarkers[key].close();
      delete this.infoContentMarkers[key];
    }
    if (this.listMarkersKey.includes(key)) {
      this.listMarkersKey.splice(this.listMarkersKey.indexOf(key), 1);
    }
  }
  /**
  * This method remove all the markers of the map created.
  */
  removeAllMarkers() {
    if (this.listMarkersKey && this.listMarkersKey.length) {
      const remainingKeys = [];
      this.listMarkersKey.forEach((key) => {
        if (key && key.indexOf('-critic-zone-') >= 0) {
          remainingKeys.push(key);
          return;
        }
        if (this.markers[key]) {
          this.markers[key].setMap(null);
        }
      });
      this.listMarkersKey = remainingKeys;
    }
  }
  /**
  * This method set the center of a map in a specific coordinates.
  * @param {number} lat (number) is the latitude in the map.
  * @param {number} lng (number) is the longitude in the map.
  * @param {number} zoom (number - optional) is the quantity of zoom in the map.
  */
  public setCenterMap(lat: number, lng: number, zoom: number = 13): void {
    const currentZoom: number = this.map.getZoom() || 0;
    if ((zoom && zoom > currentZoom)) this.map.setZoom(zoom);
    this.map.panTo(new google.maps.LatLng(lat, lng));
  }

  /**
  * Método simple para renderizar un único punto en el mapa
  * @param {number} lat Latitud del punto
  * @param {number} lng Longitud del punto
  * @param {object} options Opciones adicionales del marcador
  */
  public renderSinglePoint(lat: number, lng: number, options?: {
    key?: string,
    icon?: string,
    label?: string,
    infoWindow?: string,
    center?: boolean,
    zoom?: number,
    clearPrevious?: boolean
  }): void {
    const defaultOptions = {
      key: 'single-point',
      icon: null,
      label: null,
      infoWindow: null,
      center: true,
      zoom: 15,
      clearPrevious: true,
      ...options
    };

    // Limpiar marcador anterior si se solicita
    if (defaultOptions.clearPrevious) {
      this.removeMarker(defaultOptions.key);
    }

    // Crear el marcador
    this.createMarker(
      defaultOptions.key,
      lat,
      lng,
      defaultOptions.infoWindow,
      defaultOptions.icon,
      defaultOptions.label
    );

    // Centrar el mapa en el punto
    if (defaultOptions.center) {
      this.setCenterMap(lat, lng, defaultOptions.zoom);
    }
  }

  //GOOGLE ROUTE
  /**
  * This method create a simulated route google.
  * @param {MapLocations} locations (MapLocations) are the locations to render.
  * @param {Cargo} cargo (Cargo) is the information of the load.
  */
  processRouteGoogle(locations: MapLocations, cargo: Cargo) {
    for (let key in locations) {
      this.removePolylineGoogle(key);
      if (locations[key] && locations[key].showRouteGoogle && cargo) {
        this.freightForwarderDetailService.getRouteGoogleCargo(key).subscribe(
          (response) => {
            if (response && response['overview_polylines'] && response['overview_polylines'][0]) {
              this.routeGoogle = google.maps.geometry.encoding.decodePath(response['overview_polylines'][0]);
              this.createPolyline(key, this.routeGoogle, '#584796', 'google');
            }
          }
        );
      }
      else if (locations[key] && locations[key].routePlanItinerary && locations[key].showRoutePlan)
        this.processRoutePlanItinerary(locations[key], key);
      else
        this.routeGoogle = [];
    }
  }

  processRoutePlanItinerary(location: MapLocation, key: string) {
    if (!location || !location.routePlanItinerary) {
      return;
    }
    if (location.routePlanItinerary.overviewPolylines) {
      this.routeGoogle = google.maps.geometry.encoding.decodePath(location.routePlanItinerary.overviewPolylines);
    }
    if (this.markers && Object.keys(this.markers).some(marker => marker.indexOf(key) >= 0)) {
      Object.keys(this.markers)
        .filter((marker) => marker.indexOf(key) >= 0 && marker.indexOf(`${key}-critic-zone-`) !== 0)
        .forEach((marker) => this.removeMarker(marker));
    }

    if (location.routePlanItinerary.controlPoints && location.routePlanItinerary.controlPoints.length)
      location.routePlanItinerary.controlPoints.sort((a, b) => a.order - b.order).forEach((point, index) => {
        if (point.location && point.location.lat && point.location.lng)
          this.createMarker(`${key}-control-point-${index}`, point.location.lat, point.location.lng, null, '/assets/svg/icons/tl_ico__pin_google_maps_trip_stop.svg', `${index + 1}`);
      });

    if (location.routePlanItinerary.authorizedStops && location.routePlanItinerary.authorizedStops.length) {
      const icons = this.global.authorizedPoints.map(point => point.icon);
      location.routePlanItinerary.authorizedStops.forEach((stop, index) => {
        const icon = stop.details && stop.details.pointType && stop.details.pointType.id ? icons[Number(stop.details.pointType.id) - 1] : icons[0];
        if (stop.location && stop.location.lat && stop.location.lng)
          this.createMarker(`${key}-authorized-stop-${index}`, stop.location.lat, stop.location.lng, null, `/assets/svg/icons/${icon}`);
      });
    }

    this.renderCriticZonesIfNeeded(location.routePlanItinerary.criticZones, key);
  }

  private processInitialRoutePlanItinerary() {
    if (!this.options || !this.options.polyline) {
      return;
    }
    const locations = this.options.polyline;
    for (const key in locations) {
      if (locations[key] && locations[key].routePlanItinerary && locations[key].showRoutePlan) {
        this.processRoutePlanItinerary(locations[key], key);
      }
    }
  }

  public renderCriticZones(zones: CriticZone[], keyPrefix: string = 'critic-zone') {
    this.clearCriticZones(keyPrefix);
    if (!this.map) {
      return;
    }
    if (!zones || !zones.length) {
      this.criticZoneSignatures[keyPrefix] = this.getCriticZoneSignature(zones, keyPrefix);
      return;
    }
    zones.forEach((zone) => this.renderCriticZone(zone, keyPrefix));
    this.criticZoneSignatures[keyPrefix] = this.getCriticZoneSignature(zones, keyPrefix);
  }

  private renderCriticZonesIfNeeded(zones: CriticZone[], keyPrefix: string) {
    const signature = this.getCriticZoneSignature(zones, keyPrefix);
    if (this.criticZoneSignatures[keyPrefix] === signature) {
      return;
    }
    this.renderCriticZones(zones, keyPrefix);
  }

  private getCriticZoneSignature(zones: CriticZone[], keyPrefix: string): string {
    if (!zones || !zones.length) {
      return `${keyPrefix}:empty`;
    }
    return keyPrefix + ':' + zones.map((zone) => {
      const id = zone && zone.id ? zone.id : 'preview';
      const polylineSize = zone && zone.polylines ? zone.polylines.length : 0;
      return id + ':' + polylineSize;
    }).join('|');
  }

  public renderCriticZone(zone: CriticZone, keyPrefix: string = 'critic-zone') {
    if (!zone || !this.map) {
      return;
    }
    const zoneId = zone.id ? zone.id : 'preview';
    const overlayKey = `${keyPrefix}-critic-zone-${zoneId}`;
    this.removeCriticZoneOverlay(overlayKey);
    const path = zone.polylines ? this.planningRouteService.getCriticZonePath(zone.polylines) : [];
    const corridor = this.planningRouteService.createCriticZoneCorridor(this.map, path);
    if (corridor && corridor.length) {
      this.criticZoneCorridors[overlayKey] = corridor;
    }

    const icon = this.global.pathMarkerCriticZone;
    if (zone.start && zone.start.location) {
      this.createMarker(
        `${overlayKey}-start`,
        zone.start.location.lat,
        zone.start.location.lng,
        buildCriticZoneInfoWindow(zone, 'start'),
        icon
      );
    }
    if (zone.end && zone.end.location) {
      this.createMarker(
        `${overlayKey}-end`,
        zone.end.location.lat,
        zone.end.location.lng,
        buildCriticZoneInfoWindow(zone, 'end'),
        icon
      );
    }
  }

  public clearCriticZones(keyPrefix: string = 'critic-zone') {
    delete this.criticZoneSignatures[keyPrefix];
    const overlayPrefix = `${keyPrefix}-critic-zone-`;
    Object.keys(this.criticZoneCorridors).forEach((overlayKey) => {
      if (overlayKey.indexOf(overlayPrefix) === 0) {
        this.removeCriticZoneOverlay(overlayKey);
      }
    });
    if (this.listMarkersKey && this.listMarkersKey.length) {
      this.listMarkersKey.filter((markerKey) => markerKey.indexOf(overlayPrefix) === 0).forEach((markerKey) => {
        this.removeMarker(markerKey);
      });
    }
  }

  public fitMapToCriticZone(zone: CriticZone) {
    if (!this.map || !zone) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    if (zone.start && zone.start.location) {
      bounds.extend(new google.maps.LatLng(zone.start.location.lat, zone.start.location.lng));
    }
    if (zone.end && zone.end.location) {
      bounds.extend(new google.maps.LatLng(zone.end.location.lat, zone.end.location.lng));
    }
    if (zone.polylines) {
      const points = this.utils.decodePolyline(zone.polylines);
      points.forEach((point) => bounds.extend(new google.maps.LatLng(point.lat, point.lng)));
    }
    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds);
    }
  }

  private removeCriticZoneOverlay(overlayKey: string) {
    if (this.criticZoneCorridors[overlayKey]) {
      this.criticZoneCorridors[overlayKey].forEach((polyline) => polyline.setMap(null));
      delete this.criticZoneCorridors[overlayKey];
    }
    this.removeMarker(`${overlayKey}-start`);
    this.removeMarker(`${overlayKey}-end`);
  }


  /**
  * This method create a simulated route according to the locations.
  * @param {Array<any>} listLocations (Array<any>) is the list of locations to stimate.
  * @param {string} key (string) is the unique identifier of the marker.
  */
  processAnyGoogleRoute(listLocations: Array<any>, key: string) {
    this.googleService
      .getRouteData(listLocations)
      .then(
        (success: { cargoDistancy; cargoEstimatedTime; cargoRoute }) => {
          const overviewPolyline = (
            success.cargoRoute.routes[0].overview_polyline.points || success.cargoRoute.routes[0].overview_polyline
          );
          if (overviewPolyline) {
            this.routeGoogle = google.maps.geometry.encoding.decodePath(overviewPolyline);
            this.createPolyline(key, this.routeGoogle, '#02d7dc', 'google');
          }
        }
      )
      .catch((error) => {
        console.error(error)
      });
  }

  public renderEncodedPolyline(encodedPolyline: string, key: string = 'encoded-route', color: string = '#584796') {
    this.clearEncodedRouteByKey(key);
    if (!encodedPolyline || !encodedPolyline.trim())
      return;
    try {
      const decodedPath = google.maps.geometry.encoding.decodePath(encodedPolyline);
      if (!decodedPath || !decodedPath.length)
        return;
      this.routeGoogle = decodedPath;
      const deprioritize = this.shouldDeprioritizeItineraryRoute();
      this.createPolyline(
        key,
        this.routeGoogle,
        color,
        'google',
        deprioritize ? { strokeOpacity: 0.35, strokeWeight: 3, zIndex: 10 } : { strokeOpacity: 1, strokeWeight: 4, zIndex: 80 }
      );
      this.fitMapToPath(decodedPath);
    } catch (error) {
      console.error(error);
    }
  }

  public renderEncodedPolylineAsDirections(
    encodedPolyline: string,
    key: string = 'encoded-route',
    color: string = '#584796',
    maxWaypoints: number = 20
  ) {
    this.clearEncodedRouteByKey(key);
    if (!encodedPolyline || !encodedPolyline.trim())
      return;
    try {
      const decodedPath: any[] = google.maps.geometry.encoding.decodePath(encodedPolyline);
      if (!decodedPath || decodedPath.length < 2)
        return;

      const origin = decodedPath[0];
      const destination = decodedPath[decodedPath.length - 1];
      const sampledWaypoints = this.sampleDecodedPathWaypoints(decodedPath, maxWaypoints).map((point) => ({
        location: point,
        stopover: false
      }));
      const directionsService = new google.maps.DirectionsService();
      directionsService.route({
        origin,
        destination,
        waypoints: sampledWaypoints,
        travelMode: google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false
      }, (response, status) => {
        if (status !== 'OK' || !response || !response.routes || !response.routes.length) {
          // Fallback a polyline cuando Directions no puede reconstruir la ruta.
          this.renderEncodedPolyline(encodedPolyline, key, color);
          return;
        }
        this.clearEncodedRouteByKey(key);
        const deprioritize = this.shouldDeprioritizeItineraryRoute();
        const renderer = new google.maps.DirectionsRenderer({
          map: this.map,
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: color,
            strokeOpacity: deprioritize ? 0.35 : 1,
            strokeWeight: deprioritize ? 3 : 4,
            zIndex: deprioritize ? 10 : 80,
          }
        });
        renderer.setDirections(response);
        this.directionsRenderersGoogle[key] = renderer;
        if (response.routes[0].bounds)
          this.map.fitBounds(response.routes[0].bounds);
      });
    } catch (error) {
      console.error(error);
      this.renderEncodedPolyline(encodedPolyline, key, color);
    }
  }

  //POLYLINES
  /**
  * This method remove and specific google polyline.
  * @param {string} key (string) is the unique identifier of the polyline.
  */
  private removePolylineGoogle(key: string) {
    if (this.polylinesGoogle[key]) {
      this.polylinesGoogle[key].setMap(null);
      delete this.polylinesGoogle[key];
    }
    if (this.listPolylineGoogleKeys && this.listPolylineGoogleKeys.length) {
      this.listPolylineGoogleKeys = this.listPolylineGoogleKeys.filter((polylineKey) => polylineKey !== key);
    }
  }

  private removeDirectionsRendererGoogle(key: string) {
    if (this.directionsRenderersGoogle[key]) {
      this.directionsRenderersGoogle[key].setMap(null);
      delete this.directionsRenderersGoogle[key];
    }
  }
  /**
  * This method remove and specific real route polyline.
  * @param {string} key (string) is the unique identifier of the polyline.
  */
  private removePolylineReal(key: string) {
    if (this.polylines[key]) {
      this.polylines[key].setMap(null);
      delete this.polylines[key];
    }
    if (this.listPolylineKeys && this.listPolylineKeys.length) {
      this.listPolylineKeys = this.listPolylineKeys.filter((polylineKey) => polylineKey !== key);
    }
  }
  /**
  * This method remove all the realm polylines created.
  */
  private removeAllPolylineReal() {
    if (this.listPolylineKeys) {
      for (let i = 0; i < this.listPolylineKeys.length; i++) {
        let key = this.listPolylineKeys[i];
        if (this.polylines[key]) {
          this.polylines[key].setMap(null);
          this.listPolylineKeys.splice(i, 1);
          i--;
        }
      }
    }
  }
  /**
  * This method create a polyline according to a path and a color sended.
  * @param {google.maps.LatLngLiteral[]} path (google.maps.LatLngLiteral[]) is the array of elements to create the polyline.
  * @param {string} strokeColor (string) is the color of the polyline.
  * @returns {google.maps.Polyline} (google.maps.Polyline) returns the instance of polyline created.
  */
  public polyline(path: google.maps.LatLngLiteral[], strokeColor: string): google.maps.Polyline {
    const polyline = new google.maps.Polyline(
      {
        path,
        strokeColor,
        strokeOpacity: 1.0,
        strokeWeight: 2,
        geodesic: true,
      }
    );
    polyline.setMap(this.map);
    return polyline;
  }
  /**
  * This method create a new polyline instance of google.
  * @param {string} key (string) is the unique identifier of the marker.
  * @param {any} locations (any) is the object of location points that wanted to render.
  * @param {string} color (string - optional) is the color of the polyline.
  * @param {string} type (string) is the type of polyline that want to render could be: real or google.
  */
  private async createPolyline(
    key: string,
    locations: any,
    color: string,
    type: string,
    options?: { strokeOpacity?: number, strokeWeight?: number, zIndex?: number }
  ) {
    if (locations.length) {
      let setpolylines;
      setpolylines = type && type === 'real' ? this.polylines : this.polylinesGoogle;
      const locationsWithLatAndLng = locations.filter(location => location.lat && location.lng);
      setpolylines[key] = new google.maps.Polyline({
        path: locationsWithLatAndLng,
        geodesic: false,
        strokeColor: color,
        strokeOpacity: options && options.strokeOpacity !== undefined ? options.strokeOpacity : 1.0,
        strokeWeight: options && options.strokeWeight !== undefined ? options.strokeWeight : 2,
        zIndex: options && options.zIndex !== undefined ? options.zIndex : (type === 'real' ? 100 : 50),
      });
      if (type === 'real') {
        this.polylines[key].setMap(this.map);
        if (!this.listPolylineKeys.includes(key))
          this.listPolylineKeys.push(key);
      } else if (type === 'google') {
        this.polylinesGoogle[key].setMap(this.map);
        if (!this.listPolylineGoogleKeys.includes(key))
          this.listPolylineGoogleKeys.push(key);
      }
    }
  }

  private clearEncodedRouteByKey(key: string) {
    this.removeDirectionsRendererGoogle(key);
    this.removePolylineGoogle(key);
  }
  /**
  * This method remove or create polylines.
  * @param {MapLocations} locations (MapLocations) is the object of location points that wanted to render.
  * @param {string} type (string) is the type of polyline that want to render could be: real or google.
  * @param {string} color (string - optional) is the color of the polyline.
  */
  checkPolyline(locations: MapLocations, type: string, color?: string) {
    if (locations) {
      if (this.options.removeAllPolylines) this.removeAllPolylineReal();
      for (let key in locations) {
        this.removePolylineReal(key)
        if (locations[key] && locations[key].path && locations[key].path.length && locations[key].showPolyline) {
          const path = locations[key].path;
          this.createPolyline(key, path, color, type);
        }
      }
    }
  }

  private fitMapToPath(path: any[]) {
    if (!this.map || !path || !path.length)
      return;
    const bounds = new google.maps.LatLngBounds();
    path.forEach((location) => {
      if (!location)
        return;
      const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
      const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
      if (lat !== undefined && lat !== null && lng !== undefined && lng !== null)
        bounds.extend(new google.maps.LatLng(lat, lng));
    });
    if (!bounds.isEmpty())
      this.map.fitBounds(bounds);
  }

  private sampleDecodedPathWaypoints(path: any[], maxWaypoints: number): any[] {
    if (!path || path.length <= 2)
      return [];
    const middle = path.slice(1, path.length - 1);
    if (!middle.length)
      return [];
    if (middle.length <= maxWaypoints)
      return middle;
    const step = Math.ceil(middle.length / maxWaypoints);
    const sampled = [];
    for (let i = 0; i < middle.length; i += step) {
      sampled.push(middle[i]);
      if (sampled.length >= maxWaypoints)
        break;
    }
    return sampled;
  }

  private shouldDeprioritizeItineraryRoute(): boolean {
    const polylineOptions = this.options && this.options.polyline ? this.options.polyline : null;
    if (!polylineOptions)
      return false;
    for (const key in polylineOptions) {
      if (
        polylineOptions[key] &&
        polylineOptions[key].showPolyline &&
        polylineOptions[key].path &&
        polylineOptions[key].path.length > 1
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Dibuja un polígono GeoJSON (Polygon o MultiPolygon) sobre el mapa,
   * típicamente el contorno del municipio DIVIPOLA seleccionado por el usuario.
   * Múltiples polígonos coexisten identificados por `key`. Si la key ya existía,
   * el polígono anterior con esa key se reemplaza.
   *
   * @param geometry GeoJSON con coordenadas en [lng, lat]
   * @param options.key identificador del polígono (default 'default')
   * @param options.color color del trazo y relleno (default azul Tailwind)
   * @param options.fillOpacity opacidad del relleno (0-1, default 0.12)
   * @param options.fit si true, hace fitBounds del mapa abarcando TODOS los polígonos vigentes (default true)
   */
  public renderMunicipalityPolygon(
    geometry: { type: string; coordinates: any } | null | undefined,
    options?: { key?: string; color?: string; fillOpacity?: number; fit?: boolean }
  ): void {
    const key = (options && options.key) || 'default';
    this.removeMunicipalityPolygon(key);
    if (!geometry || !geometry.coordinates || !this.map) return;

    const color = (options && options.color) || '#3b82f6';
    const fillOpacity = (options && options.fillOpacity != null) ? options.fillOpacity : 0.12;
    const shouldFit = !options || options.fit !== false;

    const paths: google.maps.LatLngLiteral[][] = [];
    const pushRing = (ring: any[]) => {
      paths.push(ring.map((coord: any) => ({ lat: coord[1], lng: coord[0] })));
    };
    if (geometry.type === 'Polygon') {
      geometry.coordinates.forEach((ring: any[]) => pushRing(ring));
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach((poly: any[]) => poly.forEach((ring: any[]) => pushRing(ring)));
    } else {
      return;
    }
    if (!paths.length) return;

    const polygon = new google.maps.Polygon({
      paths,
      strokeColor: color,
      strokeOpacity: 0.85,
      strokeWeight: 2,
      fillColor: color,
      fillOpacity,
      map: this.map,
      clickable: false,
      zIndex: 1,
    });
    this.municipalityPolygons.set(key, polygon);

    if (shouldFit) {
      const bounds = new google.maps.LatLngBounds();
      this.municipalityPolygons.forEach(poly => {
        poly.getPaths().forEach(path => path.forEach(p => bounds.extend(p)));
      });
      if (!bounds.isEmpty()) this.map.fitBounds(bounds, 32);
    }
  }

  /**
   * Elimina polígonos municipales del mapa.
   * - Sin `key`: borra TODOS los polígonos vigentes.
   * - Con `key`: borra solo el polígono con esa key (si existe).
   */
  public removeMunicipalityPolygon(key?: string): void {
    if (key === undefined) {
      this.municipalityPolygons.forEach(poly => poly.setMap(null));
      this.municipalityPolygons.clear();
      return;
    }
    const polygon = this.municipalityPolygons.get(key);
    if (polygon) {
      polygon.setMap(null);
      this.municipalityPolygons.delete(key);
    }
  }

  ngOnDestroy() {
    this.removeMunicipalityPolygon();
    this.cdRef.detach();
  }
}
